import { useState, useCallback } from "react";
import type { PublicContent } from "./useApiQueries.js";
import { useRecentlyViewed } from "./useRecentlyViewed.js";

function deriveYouTubeEmbed(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  return m?.[1] ? `https://www.youtube.com/embed/${m[1]}` : null;
}

/**
 * Tiny shared wrapper for the in-place video player. Pages render
 * <VideoLightbox {...lightboxProps} /> once and pass `openFor` as the
 * `onVideoClick` prop to any ContentCard. Click → modal, no navigation.
 *
 * Also tracks the opened item to "recently viewed" so it surfaces on Home.
 */
export function useVideoLightbox() {
  const [video, setVideo] = useState<PublicContent | null>(null);
  const { track: trackRecent } = useRecentlyViewed();

  const openFor = useCallback(
    (item: PublicContent) => {
      setVideo(item);
      trackRecent({
        id: item.id,
        slug: item.slug,
        title: item.title,
        type: item.type,
        thumbnailUrl: item.thumbnailUrl,
        sourceAuthor: item.sourceAuthor,
        publishedAt: item.publishedAt,
      });
    },
    [trackRecent],
  );
  const close = useCallback(() => setVideo(null), []);

  const lightboxProps = {
    open: !!video,
    onClose: close,
    embedUrl: video ? video.embedUrl ?? deriveYouTubeEmbed(video.sourceUrl) : null,
    title: video?.title ?? "",
    detailSlug: video?.slug ?? "",
  };

  return { openFor, lightboxProps };
}
